import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Calendar, Clock, User, Tag, Share2 } from 'lucide-react';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Find the blog post
  const post = t.blog.items.find(item => item.slug === slug);

  // Current URL
  const currentUrl = `https://adidoo.com/${language}/blog/${slug}`;
  const alternateLang = language === 'en' ? 'ar' : 'en';
  const alternateUrl = `https://adidoo.com/${alternateLang}/blog/${slug}`;

  // Format date for schema
  const formatDateForSchema = (dateString) => {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  };

  // Update meta tags and structured data
  useEffect(() => {
    if (!post) return;

    // Update page title
    document.title = `${post.title} | Adidoo Blog`;

    // Helper function to update or create meta tags
    const updateMetaTag = (name, content, attribute = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel, href, hreflang = null) => {
      let selector = `link[rel="${rel}"]`;
      if (hreflang) {
        selector = `link[rel="${rel}"][hreflang="${hreflang}"]`;
      }
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        if (hreflang) {
          element.setAttribute('hreflang', hreflang);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic meta tags
    updateMetaTag('description', post.excerpt);
    updateMetaTag('keywords', post.tags.join(', '));
    updateMetaTag('author', post.author);
    updateMetaTag('robots', 'index, follow, max-snippet:-1, max-image-preview:large');

    // Open Graph tags
    updateMetaTag('og:title', post.title, 'property');
    updateMetaTag('og:description', post.excerpt, 'property');
    updateMetaTag('og:type', 'article', 'property');
    updateMetaTag('og:url', currentUrl, 'property');
    updateMetaTag('og:image', post.image, 'property');
    updateMetaTag('og:image:width', '1200', 'property');
    updateMetaTag('og:image:height', '630', 'property');
    updateMetaTag('og:site_name', 'Adidoo', 'property');
    updateMetaTag('og:locale', language === 'en' ? 'en_US' : 'ar_SA', 'property');
    updateMetaTag('article:published_time', formatDateForSchema(post.date), 'property');
    updateMetaTag('article:author', post.author, 'property');
    updateMetaTag('article:section', post.category, 'property');

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', 'name');
    updateMetaTag('twitter:title', post.title, 'name');
    updateMetaTag('twitter:description', post.excerpt, 'name');
    updateMetaTag('twitter:image', post.image, 'name');

    // Canonical and alternate links
    updateLinkTag('canonical', currentUrl);
    updateLinkTag('alternate', alternateUrl, alternateLang);

    // Structured Data - BlogPosting
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": {
        "@type": "ImageObject",
        "url": post.image,
        "width": 1200,
        "height": 630
      },
      "author": {
        "@type": "Person",
        "name": post.author,
        "jobTitle": post.authorRole,
        "worksFor": {
          "@type": "Organization",
          "name": "Adidoo"
        }
      },
      "publisher": {
        "@type": "Organization",
        "name": "Adidoo",
        "url": "https://adidoo.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://adidoo.com/logo.png",
          "width": 200,
          "height": 60
        }
      },
      "datePublished": formatDateForSchema(post.date),
      "dateModified": formatDateForSchema(post.date),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": currentUrl
      },
      "keywords": post.tags.join(', '),
      "articleSection": post.category,
      "inLanguage": language === 'en' ? 'en-US' : 'ar-SA',
      "url": currentUrl
    };

    // Breadcrumb Structured Data
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": language === 'en' ? "Home" : "الرئيسية",
          "item": `https://adidoo.com/${language}`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": language === 'en' ? "Blog" : "المدونة",
          "item": `https://adidoo.com/${language}#blog`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": post.title,
          "item": currentUrl
        }
      ]
    };

    // Add or update structured data scripts
    let blogPostingScript = document.getElementById('blog-posting-schema');
    if (!blogPostingScript) {
      blogPostingScript = document.createElement('script');
      blogPostingScript.id = 'blog-posting-schema';
      blogPostingScript.type = 'application/ld+json';
      document.head.appendChild(blogPostingScript);
    }
    blogPostingScript.textContent = JSON.stringify(structuredData);

    let breadcrumbScript = document.getElementById('breadcrumb-schema');
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.id = 'breadcrumb-schema';
      breadcrumbScript.type = 'application/ld+json';
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbData);

    // Scroll to top
    window.scrollTo(0, 0);

    // Cleanup function
    return () => {
      // Remove structured data scripts when component unmounts
      const blogScript = document.getElementById('blog-posting-schema');
      const breadScript = document.getElementById('breadcrumb-schema');
      if (blogScript) blogScript.remove();
      if (breadScript) breadScript.remove();
    };
  }, [post, slug, language, currentUrl, alternateUrl, alternateLang]);

  // If post not found
  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Blog Post Not Found' : 'المقال غير موجود'}
          </h1>
          <Button onClick={() => navigate(`/${language}`)}>
            {language === 'en' ? 'Back to Home' : 'العودة إلى الرئيسية'}
          </Button>
        </div>
      </div>
    );
  }

  // Share function
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: currentUrl,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(currentUrl);
      alert(language === 'en' ? 'Link copied to clipboard!' : 'تم نسخ الرابط!');
    }
  };

  return (
    <article className="min-h-screen bg-gray-50 pt-32 pb-16" itemScope itemType="https://schema.org/BlogPosting">
      {/* Hidden structured data */}
      <meta itemProp="headline" content={post.title} />
      <meta itemProp="description" content={post.excerpt} />
      <meta itemProp="datePublished" content={formatDateForSchema(post.date)} />
      <meta itemProp="dateModified" content={formatDateForSchema(post.date)} />
      <meta itemProp="image" content={post.image} />
      <meta itemProp="author" content={post.author} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button & Share */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(`/${language}`)}
            className="hover:bg-teal-50 hover:text-teal-600"
            aria-label={language === 'en' ? 'Back to home' : 'العودة إلى الرئيسية'}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Back to Home' : 'العودة إلى الرئيسية'}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleShare}
            className="hover:bg-teal-50 hover:text-teal-600"
            aria-label={language === 'en' ? 'Share article' : 'مشاركة المقال'}
          >
            <Share2 className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Share' : 'مشاركة'}
          </Button>
        </div>

        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-gray-600">
            <li>
              <button 
                onClick={() => navigate(`/${language}`)}
                className="hover:text-teal-600 transition-colors"
              >
                {language === 'en' ? 'Home' : 'الرئيسية'}
              </button>
            </li>
            <li>/</li>
            <li>
              <button 
                onClick={() => navigate(`/${language}#blog`)}
                className="hover:text-teal-600 transition-colors"
              >
                {language === 'en' ? 'Blog' : 'المدونة'}
              </button>
            </li>
            <li>/</li>
            <li className="text-gray-900 font-medium line-clamp-1">{post.category}</li>
          </ol>
        </nav>

        {/* Category Badge */}
        <Badge className="mb-4 bg-teal-600 hover:bg-teal-700">
          {post.category}
        </Badge>

        {/* Title */}
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight" itemProp="headline">
          {post.title}
        </h1>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8 pb-8 border-b">
          <div className="flex items-center gap-2" itemProp="author" itemScope itemType="https://schema.org/Person">
            <User className="w-5 h-5" />
            <div>
              <p className="font-semibold text-gray-900" itemProp="name">{post.author}</p>
              <p className="text-sm text-gray-500" itemProp="jobTitle">{post.authorRole}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <time dateTime={formatDateForSchema(post.date)} itemProp="datePublished">
              {post.date}
            </time>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            <span>{post.readTime}</span>
          </div>
        </div>

        {/* Featured Image */}
        <figure className="mb-12 rounded-xl overflow-hidden shadow-2xl" itemProp="image" itemScope itemType="https://schema.org/ImageObject">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-96 object-cover"
            itemProp="url"
            loading="eager"
          />
          <meta itemProp="width" content="1200" />
          <meta itemProp="height" content="630" />
        </figure>

        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-xl text-gray-700 leading-relaxed" itemProp="description">
            {post.content.intro}
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8" itemProp="articleBody">
          {post.content.sections.map((section, index) => (
            <section key={index} className="bg-white rounded-xl p-8 shadow-md">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {section.heading}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {section.text}
              </p>
            </section>
          ))}
        </div>

        {/* Conclusion */}
        <div className="mt-12 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-8 border-l-4 border-teal-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Conclusion' : 'الخلاصة'}
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            {post.content.conclusion}
          </p>
        </div>

        {/* Tags */}
        <div className="mt-12 flex flex-wrap items-center gap-2" itemProp="keywords">
          <Tag className="w-5 h-5 text-gray-500" aria-hidden="true" />
          {post.tags.map((tag, index) => (
            <Badge key={index} variant="outline" className="text-gray-700">
              {tag}
            </Badge>
          ))}
        </div>

        {/* Author Bio */}
        <div className="mt-12 bg-white rounded-xl p-8 shadow-md border-l-4 border-teal-600" itemProp="author" itemScope itemType="https://schema.org/Person">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {post.author.charAt(0)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1" itemProp="name">
                {post.author}
              </h3>
              <p className="text-gray-600 mb-2" itemProp="jobTitle">
                {post.authorRole}
              </p>
              <p className="text-gray-700">
                {language === 'en' 
                  ? `${post.author} is an expert at Adidoo with extensive experience in ${post.category}.`
                  : `${post.author} خبير في أديدو ولديه خبرة واسعة في ${post.category}.`}
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            {language === 'en' 
              ? 'Ready to Start Your Project?' 
              : 'مستعد لبدء مشروعك؟'}
          </h3>
          <p className="text-lg mb-6 opacity-90">
            {language === 'en'
              ? 'Let\'s discuss how we can help your business grow with technology.'
              : 'دعنا نناقش كيف يمكننا مساعدة عملك على النمو بالتقنية.'}
          </p>
          <Button
            size="lg"
            onClick={() => navigate(`/${language}/contact`)}
            className="bg-white text-teal-600 hover:bg-gray-100"
          >
            {language === 'en' ? 'Contact Us' : 'اتصل بنا'}
          </Button>
        </div>

        {/* Related Posts Navigation */}
        <div className="mt-16 pt-8 border-t">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            {language === 'en' ? 'More Articles' : 'المزيد من المقالات'}
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {t.blog.items
              .filter(item => item.slug !== slug)
              .slice(0, 2)
              .map((relatedPost) => (
                <article
                  key={relatedPost.slug}
                  onClick={() => navigate(`/${language}/blog/${relatedPost.slug}`)}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer group"
                  itemScope
                  itemType="https://schema.org/BlogPosting"
                >
                  <Badge className="mb-3 bg-teal-600 hover:bg-teal-700">
                    {relatedPost.category}
                  </Badge>
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2" itemProp="headline">
                    {relatedPost.title}
                  </h4>
                  <p className="text-gray-600 line-clamp-2 mb-4" itemProp="description">
                    {relatedPost.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <time itemProp="datePublished">{relatedPost.date}</time>
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {relatedPost.readTime}
                    </span>
                  </div>
                  <meta itemProp="author" content={relatedPost.author} />
                  <meta itemProp="image" content={relatedPost.image} />
                </article>
              ))}
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogPost;